import { useEffect, useRef } from "react";
import { tutorApi } from "@/utils/axios-utils";
import {
  trackLessonEnd,
  trackLessonStart,
  trackSlideEnd,
  trackSlideStart,
} from "@/utils/mixpanel-utils";
import { recordLessonStartupPerf } from "@/features/tutor/utils/lessonStartupPerf";
import { useLatestRef } from "./useLatestRef";

export function useLessonAnalytics({
  currentLessonId,
  currentLessonName,
  currentSessionId,
  currentSlideIndex,
  enabled = true,
  getSlideByIndex,
  slides,
}) {
  const previousSlideIndexRef = useRef(null);
  const observedSlideIndexRef = useRef(null);
  const suppressedSlideTrackKeyRef = useRef(null);
  const analyticsRef = useLatestRef({
    currentLessonId,
    currentLessonName,
    currentSlideIndex,
    getSlideByIndex,
    slides,
  });

  useEffect(() => {
    if (slides.length === 0 || !currentLessonName) return;

    if (!enabled) {
      const previousTrackedSlideIndex = previousSlideIndexRef.current;
      const previousObservedSlideIndex = observedSlideIndexRef.current;
      const suppressesSlideStart =
        previousTrackedSlideIndex !== null &&
        previousTrackedSlideIndex !== currentSlideIndex;
      const suppressesSlideEnd =
        previousObservedSlideIndex !== null &&
        previousObservedSlideIndex !== currentSlideIndex;
      if (suppressesSlideStart || suppressesSlideEnd) {
        const suppressKey = [
          currentLessonId,
          previousTrackedSlideIndex ?? "none",
          previousObservedSlideIndex ?? "none",
          currentSlideIndex,
          slides.length,
        ].join(":");
        if (suppressedSlideTrackKeyRef.current !== suppressKey) {
          suppressedSlideTrackKeyRef.current = suppressKey;
          recordLessonStartupPerf("slide_analytics_suppressed", {
            lesson_id: currentLessonId,
            previous_slide_index: previousTrackedSlideIndex,
            previous_observed_slide_index: previousObservedSlideIndex,
            slide_index: currentSlideIndex,
            suppressed_slide_start: suppressesSlideStart,
            suppressed_slide_end: suppressesSlideEnd,
            reason: "startup_gate",
          });
        }
      }
      observedSlideIndexRef.current = currentSlideIndex;
      return;
    }

    suppressedSlideTrackKeyRef.current = null;
    observedSlideIndexRef.current = currentSlideIndex;

    if (
      previousSlideIndexRef.current !== null &&
      previousSlideIndexRef.current !== currentSlideIndex
    ) {
      const previousSlide = getSlideByIndex(previousSlideIndexRef.current);
      if (previousSlide) {
        trackSlideEnd({
          slide_id: previousSlide.id || previousSlide.slide_index,
          lesson_name: currentLessonName,
        });
      }
    }

    if (previousSlideIndexRef.current !== currentSlideIndex) {
      const currentSlide = getSlideByIndex(currentSlideIndex);
      if (currentSlide) {
        trackSlideStart({
          slide_id: currentSlide.id || currentSlide.slide_index,
          lesson_name: currentLessonName,
        });
      }
      previousSlideIndexRef.current = currentSlideIndex;
    }
  }, [
    currentLessonId,
    currentSlideIndex,
    enabled,
    getSlideByIndex,
    slides,
    currentLessonName,
  ]);

  useEffect(() => {
    if (!currentLessonId) return;

    // Keep lesson lifecycle eager so early abandonment during boot is still counted.
    const trackCurrentLessonEnd = () => {
      const {
        currentLessonId,
        currentLessonName,
        currentSlideIndex,
        getSlideByIndex,
        slides,
      } = analyticsRef.current;
      const totalSlides = slides?.length || 0;
      const completionPercentage =
        totalSlides > 0 ? ((currentSlideIndex + 1) / totalSlides) * 100 : 0;

      const currentSlide = getSlideByIndex(currentSlideIndex);
      if (currentSlide) {
        trackSlideEnd({
          slide_id: currentSlide.id || currentSlide.slide_index,
          lesson_name: currentLessonName,
        });
      }

      trackLessonEnd({
        lesson_id: currentLessonId,
        name: currentLessonName,
        completion_percentage: completionPercentage,
        total_slides: totalSlides,
        slides_completed: currentSlideIndex + 1,
      });
    };
    const handleBeforeUnload = () => {
      trackCurrentLessonEnd();
    };

    trackLessonStart({
      lesson_id: currentLessonId,
      name: analyticsRef.current.currentLessonName,
    });

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      trackCurrentLessonEnd();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [analyticsRef, currentLessonId]);

  useEffect(() => {
    if (!enabled || !currentSessionId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        tutorApi
          .post(`/session/user_off_tab/${currentSessionId}`)
          .catch((error) => {
            console.error("Failed to notify user_off_tab:", error);
          });
      } else {
        tutorApi
          .post(`/session/user_on_tab/${currentSessionId}`)
          .catch((error) => {
            console.error("Failed to notify user_on_tab:", error);
          });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [currentSessionId, enabled]);
}
